import { useDataQueryClient } from "@Libs/DataLayer/hooks/useDataQuery";
import { useDataMutation } from "@Libs/DataLayer/hooks/useDataMutation";

import { SUBCATEGORY_SCREEN_KEY } from "../config/constants";
import {
  ICreateDraftPayload,
  ICreateDraftResponse,
  createServiceRequestDraft,
} from "../services/serviceRequestCreation";
import { setDraftToken, setLastScreen } from "./useDraftStorage";
import {
  draftQueryKey,
  useSeedAiSuggestedSCategory,
  useSeedTaskCreationConfig,
} from "./useServiceRequestDraft";

/**
 * Creates the draft from the description screen (RT-4321 M2).
 *
 * The POST is the only place the draft's token comes into existence, so success
 * has to land four things before anything navigates: the token in localStorage,
 * the draft in its own cache key, the returned `form_config` in the config cache
 * (saving the next screen a round trip), and the resume pointer.
 *
 * Deliberately **not** tagged with `DRAFT_QUERY_KEY` as its mutation key: that
 * key drives `useIsDraftSaving`, which disables the two CTAs on the per-screen
 * PUTs. This screen has one CTA and reads its own `isLoading`.
 */
export const useCreateServiceRequestDraft = () => {
  const queryClient = useDataQueryClient();
  const seedConfig = useSeedTaskCreationConfig();
  const seedAiSuggestion = useSeedAiSuggestedSCategory();

  return useDataMutation<ICreateDraftResponse, unknown, ICreateDraftPayload>(
    createServiceRequestDraft,
    {
      onSuccess: ({ service_request, form_config, ai_enrichment }) => {
        setDraftToken(service_request.signed_id);
        queryClient.setQueryData(
          draftQueryKey(service_request.signed_id),
          service_request
        );
        // Guarded: `useSeedTaskCreationConfig` reads
        // `config.category.s_category_id`, and a throw in here is routed to the
        // mutation's `onError` — which would toast "could not save" over a POST
        // that succeeded, and leave a retry creating a second draft.
        if (form_config?.category) seedConfig(form_config);
        if (ai_enrichment?.suggested_s_category_id != null) {
          seedAiSuggestion(
            service_request.signed_id,
            ai_enrichment.suggested_s_category_id
          );
        }
        setLastScreen(SUBCATEGORY_SCREEN_KEY);
      },
    }
  );
};
